import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { LAYER_CONFIGS } from '../../constants/general';
import { filterSelector } from '../../store/selectors/filters';
import { basemapSelector } from '../../store/selectors/general';
import { getIconMask } from '../../utils/mapIcons';
import { getContourColor, getIconBadgeColors } from '../../utils/map';
import { BLELegendDisplay, WSELegendDisplay, DamagePlainLegendDisplay } from './SpectrumLegendDisplays';

const LegendIcon = ({ icon, basemapInfo }) => {
  const [maskUrl, setMaskUrl] = useState(null);
  const { badge, glyph, ring } = getIconBadgeColors(basemapInfo);

  useEffect(() => {
    getIconMask(icon).then(({ dataUrl }) => setMaskUrl(dataUrl));
  }, [icon]);

  return (
    <div
      className="mr-2 flex h-6 w-6 items-center justify-center rounded-full border"
      style={{ backgroundColor: badge, borderColor: ring }}
    >
      {maskUrl && (
        <div
          className="h-4 w-4"
          style={{
            backgroundColor: glyph,
            mask: `url(${maskUrl}) center / contain no-repeat`,
            WebkitMask: `url(${maskUrl}) center / contain no-repeat`,
          }}
        />
      )}
    </div>
  );
};

export const StructureLegend = ({ layers, layerVis }) => {
  const filterInfo = useSelector(filterSelector);
  const basemapInfo = useSelector(basemapSelector);

  return (
    <div
      className="absolute bottom-6 right-1 z-[1001] max-h-[620px] overflow-y-auto rounded bg-white p-3 shadow"
      id="poi-legend"
    >
      <h4 className="mb-2 text-sm font-bold">Legend</h4>
      {Object.entries(layers || {}).map(([layerId, data]) => {
        const config = LAYER_CONFIGS[layerId];
        if (!layerVis[layerId] || !config) return null;

        if (['bleDep100', 'bleDep500'].includes(layerId) && layerVis[layerId]) {
          return (
            <BLELegendDisplay
              config={config}
              key={layerId}
              filterInfo={filterInfo}
              layerId={layerId}
            />
          );
        }
        if (['wse100', 'wse500'].includes(layerId) && layerVis[layerId]) {
          return (
            <WSELegendDisplay
              config={config}
              key={layerId}
              filterInfo={filterInfo}
              layerId={layerId}
            />
          );
        }
        if (['damagePlain'].includes(layerId) && layerVis[layerId]) {
          return (
            <DamagePlainLegendDisplay key={layerId} />
          );
        }

        // For contours layer, use basemap-dependent color
        let color = config?.colors?.fillColor || config?.style?.color;
        if (['contours2ftTile', 'terrainContour'].includes(layerId)) {
          color = getContourColor(basemapInfo);
        }

        // Color palette based layers (i.e. hurricane tracks)
        if (!color && config.colors && typeof config.colors === 'object') {
          return (
            <div key={layerId} className="mb-2">
              <span className="text-xs font-semibold">{config.name}</span>
              {Object.entries(config.colors).map(([label, stormColor]) => (
                <div key={label} className="mb-0.5 ml-2 flex items-center">
                  <div
                    className="mr-2 h-3 w-3 rounded-sm"
                    style={{ backgroundColor: stormColor }}
                  />
                  <span className="text-xs">{label}</span>
                </div>
              ))}
            </div>
          );
        }

        if (config?.icon) {
          return (
            <div key={layerId} className="mb-1 flex items-center">
              <LegendIcon icon={config.icon} basemapInfo={basemapInfo} />
              <span className="text-xs">{config?.name}</span>
            </div>
          );
        }

        return (
          <div key={layerId} className="mb-1 flex items-center">
            <div
              className={`mr-2 h-3 w-3 ${['#fff', '#ffffff', 'white'].includes(color?.toLowerCase()) ? 'border border-black' : ''}`}
              style={{ backgroundColor: color }}
            />
            <span className="text-xs">{config.name}</span>
          </div>
        );
      })}
    </div>
  );
};
